import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, ScrollView, Platform, StyleSheet, TextInput, Dimensions } from 'react-native';
import api from '../../services/api';
import AdminLayout from '../components/layout/AdminLayout';
import { Ionicons } from '@expo/vector-icons';
import { adminStyles } from '../components/styles/adminStyles';
import Table from '../components/common/Table';

const ParticipantsScreen = ({ navigation, route }) => {
  const [windowWidth, setWindowWidth] = useState(Dimensions.get('window').width);
  const [participants, setParticipants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filteredParticipants, setFilteredParticipants] = useState([]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const isSmallScreen = windowWidth < 768;
  const isMediumScreen = windowWidth >= 768 && windowWidth < 1024;

  // Filter participants whenever search text changes
  useEffect(() => {
    if (searchText) {
      const filtered = participants.filter(p =>
        Object.values(p).some(value =>
          String(value).toLowerCase().includes(searchText.toLowerCase())
        )
      );
      setFilteredParticipants(filtered);
    } else {
      setFilteredParticipants([]);
    }
  }, [searchText, participants]);

  useEffect(() => {
    fetchParticipants();
  }, []);

  useEffect(() => {
    if (route.params?.refresh) {
      fetchParticipants();
      // Clear the refresh parameter
      navigation.setParams({ refresh: undefined });
    }
  }, [route.params?.refresh]);

  const fetchParticipants = async () => {
    try {
      const response = await api.get('/admin/participants');
      if (response.data.success) {
        setParticipants(response.data.data || []);
      } else {
        throw new Error('Failed to fetch participants');
      }
    } catch (err) {
      setError('Failed to load participants');
      console.error('Error fetching participants:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      field: 'participantNumber',
      header: 'Participant ID',
      sortable: true
    },
    { field: 'name', header: 'Name', sortable: true },
    {
      field: 'dob',
      header: 'DOB',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
    },
    { field: 'fcpName', header: 'FCP', sortable: true },
    { field: 'clusterName', header: 'Cluster', sortable: true },
    {
      field: 'status',
      header: 'Status',
      sortable: true,
      render: (value) => (
        <View style={[
          styles.badge,
          value === 'Active'
            ? styles.activeBadge
            : styles.inactiveBadge
        ]}>
          <Text style={[
            styles.badgeText,
            value === 'Active'
              ? styles.activeText
              : styles.inactiveText
          ]}>
            {value}
          </Text>
        </View>
      )
    },
    {
      field: 'type',
      header: 'Type',
      sortable: true,
      render: (value) => {
        const badgeStyles = {
          Student: [styles.studentBadge, styles.studentText],
          Business: [styles.businessBadge, styles.businessText],
          Employment: [styles.employmentBadge, styles.employmentText],
          Unknown: [styles.unknownBadge, styles.unknownText]
        };
        const type = value || 'Unknown';
        const [bgStyle, textStyle] = badgeStyles[type] || badgeStyles.Unknown;
        return (
          <View style={[styles.badge, bgStyle]}>
            <Text style={[styles.badgeText, textStyle]}>
              {type}
            </Text>
          </View>
        );
      }
    },
    {
      field: 'registeredDate',
      header: 'Registered',
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      field: 'lastActive',
      header: 'Last Active',
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString()
    }
  ];

  const handleEdit = (participant) => {
    console.log('Edit participant:', participant);
  };

  const handleDelete = async (participant) => {
    if (window.confirm('Are you sure you want to delete this participant?')) {
      try {
        await api.delete(`/admin/participants/${participant.id}`);
        await fetchParticipants();
      } catch (err) {
        setError('Failed to delete participant');
        console.error('Error deleting participant:', err);
      }
    }
  };

  const handleAddNew = () => {
    navigation.navigate('ParticipantAdd');
  };

  const handleBulkUpload = async () => {
    if (Platform.OS === 'web') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          try {
            setIsLoading(true);
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.post('/admin/participants/bulk-upload', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });

            if (response.data.success) {
              const { added, skipped, errors } = response.data.data;
              let message = `Successfully imported ${added} participants.`;
              if (skipped > 0) message += `\nSkipped ${skipped} duplicates.`;
              if (errors.length > 0) message += `\nErrors in ${errors.length} rows.`;

              alert(message);
              if (errors.length > 0) {
                console.log('Bulk upload issues:', errors);
              }
            }
            await fetchParticipants();
          } catch (err) {
            setError(err.response?.data?.error || 'Failed to upload participants data');
            console.error('Error uploading participants:', err);
          } finally {
            setIsLoading(false);
          }
        }
      };
      input.click();
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/admin/participants/template/download', {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'participant_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download template');
      console.error('Error downloading template:', err);
    }
  };

  // Calculate statistics
  const totalParticipants = participants.length;
  const activeParticipants = participants.filter(p => p.status === 'Active').length;
  const participantTypes = participants.reduce((acc, p) => {
    acc[p.type] = (acc[p.type] || 0) + 1;
    return acc;
  }, {});

  const handleBack = () => {
    navigation.navigate('AdminPanel');
  };

  return (
    <AdminLayout>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            onPress={handleBack}
            style={styles.backButton}
          >
            <Ionicons name="arrow-back" size={20} color="#6B7280" />
            <Text style={styles.backButtonText}>Back</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Participant Management</Text>
          <View style={styles.spacer} />
        </View>

        <ScrollView
          style={styles.mainContent}
          contentContainerStyle={styles.scrollContent}
        >
          <View style={[styles.contentWrapper, isSmallScreen && styles.contentWrapperSmall]}>
            {/* Page Header */}
            <View style={[styles.pageHeader, isSmallScreen && styles.pageHeaderSmall]}>
              <View style={isSmallScreen && { marginBottom: 12 }}>
                <Text style={[styles.pageTitle, isSmallScreen && { fontSize: 20 }]}>
                  Participants
                </Text>
                {!isSmallScreen && (
                  <Text style={styles.pageSubtitle}>
                    Manage and track all registered participants
                  </Text>
                )}
              </View>
              <View style={[styles.actionButtons, isSmallScreen && styles.actionButtonsSmall]}>
                <TouchableOpacity
                  onPress={handleAddNew}
                  style={styles.primaryButton}
                >
                  <Ionicons name="add" size={20} color="#FFFFFF" />
                  <Text style={styles.primaryButtonText}>
                    Add New Participant
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={handleBulkUpload}
                  style={styles.secondaryButton}
                >
                  <Ionicons name="cloud-upload" size={20} color="#2563EB" />
                  <Text style={styles.secondaryButtonText}>
                    Upload Bulk Data
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={handleDownloadTemplate}
                  style={styles.outlineButton}
                >
                  <Ionicons name="download" size={20} color="#6B7280" />
                  <Text style={styles.outlineButtonText}>
                    Template
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Stats Grid */}
            <View style={styles.statsCard}>
              <View style={styles.statsGrid}>
                <View style={[styles.statCard, { backgroundColor: '#EFF6FF' }]}>
                  <View style={styles.statHeader}>
                    <Text style={styles.statLabel}>Total</Text>
                    <Ionicons name="people" size={20} color="#2563EB" />
                  </View>
                  <Text style={styles.statValue}>{totalParticipants}</Text>
                </View>

                <View style={[styles.statCard, { backgroundColor: '#ECFDF5' }]}>
                  <View style={styles.statHeader}>
                    <Text style={styles.statLabel}>Active</Text>
                    <Ionicons name="checkmark-circle" size={20} color="#059669" />
                  </View>
                  <Text style={styles.statValue}>{activeParticipants}</Text>
                </View>

                <View style={[styles.statCard, { backgroundColor: '#EEF2FF' }]}>
                  <View style={styles.statHeader}>
                    <Text style={styles.statLabel}>Students</Text>
                    <Ionicons name="school" size={20} color="#4F46E5" />
                  </View>
                  <Text style={styles.statValue}>{participantTypes.Student || 0}</Text>
                </View>

                <View style={[styles.statCard, { backgroundColor: '#F5F3FF' }]}>
                  <View style={styles.statHeader}>
                    <Text style={styles.statLabel}>Business</Text>
                    <Ionicons name="business" size={20} color="#7C3AED" />
                  </View>
                  <Text style={styles.statValue}>{participantTypes.Business || 0}</Text>
                </View>

                <View style={[styles.statCard, { backgroundColor: '#FFF7ED' }]}>
                  <View style={styles.statHeader}>
                    <Text style={styles.statLabel}>Employment</Text>
                    <Ionicons name="briefcase" size={20} color="#EA580C" />
                  </View>
                  <Text style={styles.statValue}>{participantTypes.Employment || 0}</Text>
                </View>
              </View>
            </View>

            {/* Content Section */}
            <View style={styles.contentCard}>
              {error && (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              )}

              {isLoading ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="large" color="#2563EB" />
                  <Text style={styles.loadingText}>Loading participants...</Text>
                </View>
              ) : (
                <View style={styles.tableContainer}>
                  <View style={styles.searchContainer}>
                    <Ionicons
                      name="search"
                      size={20}
                      color="#6B7280"
                      style={styles.searchIcon}
                    />
                    <TextInput
                      style={styles.searchInput}
                      placeholder="Search participants..."
                      placeholderTextColor="#9CA3AF"
                      value={searchText}
                      onChangeText={setSearchText}
                    />
                  </View>
                  <Table
                    columns={columns}
                    data={searchText ? filteredParticipants : participants}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    searchable={false}
                  />
                </View>
              )}
            </View>
          </View>
        </ScrollView>
      </View>
    </AdminLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F4F6'
  },
  mainContent: {
    flex: 1
  },
  scrollContent: {
    paddingBottom: 16
  },
  contentWrapper: {
    padding: 16,
    width: '100%'
  },
  contentWrapperSmall: {
    padding: 12
  },
  pageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  pageHeaderSmall: {
    flexDirection: 'column',
    alignItems: 'stretch'
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#111827'
  },
  pageSubtitle: {
    marginTop: 4,
    fontSize: 14,
    color: '#6B7280'
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 8
  },
  actionButtonsSmall: {
    flexDirection: 'column',
    gap: 8
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2563EB',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6
  },
  primaryButtonText: {
    marginLeft: 8,
    color: '#FFFFFF',
    fontWeight: '500',
    fontSize: 14
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#2563EB'
  },
  secondaryButtonText: {
    marginLeft: 8,
    color: '#2563EB',
    fontWeight: '500',
    fontSize: 14
  },
  outlineButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#D1D5DB'
  },
  outlineButtonText: {
    marginLeft: 8,
    color: '#6B7280',
    fontWeight: '500',
    fontSize: 14
  },
  statsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    marginBottom: 16,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    margin: -6
  },
  statCard: {
    flex: 1,
    backgroundColor: '#EFF6FF',
    borderRadius: 6,
    padding: 12,
    margin: 6,
    minWidth: Platform.OS === 'web' ? 170 : '100%',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 1,
      },
      android: {
        elevation: 1,
      },
      web: {
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      },
    }),
  },
  statHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  statLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
  },
  statValue: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  contentCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1
  },
  tableContainer: {
    paddingHorizontal: 12,
    paddingTop: 0,
    paddingBottom: 12
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 6,
    paddingHorizontal: 12,
    marginBottom: 2,
    height: 36,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 1
  },
  searchIcon: {
    marginRight: 8
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#111827',
    padding: 0,
    height: '100%'
  },
  header: {
    backgroundColor: '#FFFFFF',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButtonText: {
    marginLeft: 8,
    color: '#6B7280',
    fontSize: 14,
    fontWeight: '500',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  spacer: {
    width: 40,
  },
  // Badge styles
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '500',
  },
  activeBadge: {
    backgroundColor: '#DEF7EC',
  },
  activeText: {
    color: '#03543F',
  },
  inactiveBadge: {
    backgroundColor: '#FEF3C7',
  },
  inactiveText: {
    color: '#92400E',
  },
  studentBadge: {
    backgroundColor: '#DBEAFE',
  },
  studentText: {
    color: '#1E40AF',
  },
  businessBadge: {
    backgroundColor: '#F3E8FF',
  },
  businessText: {
    color: '#5B21B6',
  },
  employmentBadge: {
    backgroundColor: '#FFF7ED',
  },
  employmentText: {
    color: '#9A3412',
  },
  unknownBadge: {
    backgroundColor: '#F3F4F6',
  },
  unknownText: {
    color: '#6B7280',
  },
  errorContainer: {
    margin: 12,
    padding: 12,
    backgroundColor: '#FEE2E2',
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: '#EF4444',
  },
  errorText: {
    color: '#B91C1C',
    fontSize: 14,
  },
  loadingContainer: {
    padding: 32,
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#6B7280',
  }
});

export default ParticipantsScreen;
